import { Flex, Tabs, Text } from "@mantine/core";
import { Link, Navigate } from "@tanstack/react-router";
import { useMemo } from "react";

import NoDataView from "~/components/NoDataView";
import { useRenderedPathname } from "~/hooks/useRenderedPathname";

export type RouteTab = {
  element: React.ReactNode;
  label: string;
  path: string;
  to?: string;
  /**
   * default false — when this tab is active, `min-height: 0` is set on the
   * outer column so a `flex: 1` element (e.g. a MeqTable with `fillHeight`) can
   * shrink to the available height and scroll internally instead of growing the
   * whole page. Per-tab so content-sized sibling tabs keep their natural
   * overflow behaviour.
   */
  fillHeight?: boolean;
};

type RouteTabPageProps = {
  basename: string;
  right?: React.ReactNode;
  shouldUseDefaultTab?: boolean;
  tabs: RouteTab[];
};

/**
 * Tab page for sections whose tab sets are org/role/view dependent — the
 * section component owns the tab config (labels, paths AND elements), so the
 * same URLs can render different content per view (e.g. admin vs producer).
 * Sections with a static 1:1 URL-to-component mapping use real child routes
 * with SectionTabsLayout instead.
 */
export const RouteTabPage = ({
  basename,
  right,
  tabs,
  shouldUseDefaultTab = false,
}: RouteTabPageProps) => {
  const pathname = useRenderedPathname();

  const matchedRoute = useMemo(() => {
    // Remainder of the pathname below the section root: "/" for the root,
    // "/beef" for a tab. Tab paths are static segments, so matching is plain
    // string equality (formerly react-router's matchRoutes with a basename).
    const remainder = pathname.startsWith(basename)
      ? pathname.slice(basename.length) || "/"
      : pathname;
    const normalizedRemainder =
      remainder.length > 1 && remainder.endsWith("/")
        ? remainder.slice(0, -1)
        : remainder;
    const matched = tabs.find((tab) => tab.path === normalizedRemainder);
    if (matched) {
      return matched;
    }
    if (shouldUseDefaultTab) {
      return tabs[0];
    }
  }, [basename, pathname, shouldUseDefaultTab, tabs]);

  if (tabs.length === 0) {
    return <NoDataView />;
  }

  return (
    <Flex
      direction="column"
      gap={24}
      flex={1}
      mih={matchedRoute?.fillHeight ? 0 : undefined}
    >
      <Tabs value={matchedRoute?.path ?? null}>
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Tab
              component={Link}
              key={tab.label}
              // Polymorphic Link props are not part of TabsTabProps. Tab URLs
              // are runtime strings living under this section's splat route,
              // so they bypass the typed `to`.
              {...{ to: `${basename}${tab.to ?? tab.path}` }}
              value={tab.path}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
          {right && (
            // Auto cross-axis margins center the slot in the tab row —
            // otherwise it stretches to the list's full height and sits
            // flush on the bottom divider, unlike the padded tab labels.
            <Flex ml="auto" my="auto">
              {right}
            </Flex>
          )}
        </Tabs.List>
      </Tabs>
      {matchedRoute?.element ?? (
        <Flex direction="column" gap={24} align="center" ta="center">
          <Text fz="3rem" fw={700} c="var(--mantine-primary-color-filled)">
            &#128519; Oops!
          </Text>
          <Text fz="1.5rem" fw={700} c="var(--mantine-primary-color-filled)">
            404 Section Not Found
          </Text>
          <Navigate to={`${basename}${tabs[0].path}` as never} replace />
        </Flex>
      )}
    </Flex>
  );
};
